import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { LogOut, Search } from 'lucide-react';
import { LogoMark } from '../components/Logo';
import EnquiryCard from '../components/EnquiryCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function startLogin() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + '/admin';
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

function LoginScreen({ error }) {
  return (
    <div data-testid="admin-login-screen" className="min-h-screen bg-[#053e49] flex items-center justify-center px-6">
      <div className="max-w-md w-full text-center">
        <div className="flex justify-center"><LogoMark size={64} /></div>
        <div className="eyebrow text-[#c3a66d] mt-8">Sunday Mirror · Private</div>
        <h1 className="font-serif text-4xl text-[#ffeabf] mt-4">Enquiries Desk</h1>
        <p className="text-[#d8d0bf] mt-4">Sign in with the approved Google account to view wedding enquiries.</p>
        {error && <p data-testid="admin-login-error" className="mt-6 text-sm text-[#ffb4a8] border border-[#ffb4a8]/40 px-4 py-3">{error}</p>}
        <button data-testid="admin-google-login-button" onClick={startLogin} className="btn-gold mt-10 mx-auto">Sign in with Google</button>
      </div>
    </div>
  );
}

function useEnquiries(enabled, q, onAuthFail) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!enabled) return undefined;
    setLoading(true);
    const t = setTimeout(() => {
      axios.get(`${API}/admin/enquiries`, { params: { q }, withCredentials: true })
        .then(({ data }) => setItems(data))
        .catch((err) => onAuthFail(err.response?.data?.detail || 'Could not load enquiries. Please sign in again.'))
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [enabled, q, onAuthFail]);
  return { items, loading };
}

export default function AdminEnquiries() {
  const location = useLocation();
  const navigate = useNavigate();
  const [initial] = useState(() => location.state || {});
  const [user, setUser] = useState(initial.user || null);
  const [authState, setAuthState] = useState(initial.user ? 'ok' : (initial.authError ? 'out' : 'checking'));
  const [error, setError] = useState(initial.authError || '');
  const [q, setQ] = useState('');
  const onAuthFail = useCallback((msg) => { setError(msg); setAuthState('out'); }, []);
  const { items, loading } = useEnquiries(authState === 'ok', q, onAuthFail);

  useEffect(() => {
    if (location.state) navigate('/admin', { replace: true, state: null });
  }, [location.state, navigate]);

  useEffect(() => {
    if (initial.user || initial.authError) return;
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then(({ data }) => { setUser(data); setAuthState('ok'); })
      .catch((err) => { if (err.response?.status === 403) setError(err.response.data.detail); setAuthState('out'); });
  }, [initial]);

  const logout = useCallback(async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch {
      // session is dropped locally either way
    } finally {
      setUser(null); setError(''); setAuthState('out');
    }
  }, []);

  if (authState === 'checking') return <div data-testid="admin-loading" className="min-h-screen bg-[#053e49]" />;
  if (authState !== 'ok') return <LoginScreen error={error} />;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#faf4ea]">
      <header className="bg-[#053e49] text-[#faf4ea]">
        <div className="max-w-[1240px] mx-auto px-6 md:px-10 py-6 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4"><LogoMark size={36} /><span className="font-serif text-xl text-[#ffeabf]">Enquiries Desk</span></div>
          <div className="flex items-center gap-4 text-sm">
            <span data-testid="admin-user-email" className="hidden sm:inline text-[#d8d0bf]">{user?.email}</span>
            <button data-testid="admin-logout-button" onClick={logout} className="flex items-center gap-2 text-[#c3a66d] hover:text-[#ffeabf] transition-colors"><LogOut size={16} />Sign out</button>
          </div>
        </div>
      </header>
      <main className="max-w-[1240px] mx-auto px-6 md:px-10 py-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div>
            <div className="eyebrow text-[#a6894f]">Wedding Enquiries</div>
            <h1 className="font-serif text-4xl text-[#053e49] mt-3"><span data-testid="admin-enquiry-count">{items.length}</span> {q ? 'matching' : 'received'}</h1>
          </div>
          <label className="flex items-center gap-3 border-b border-[#0b5260]/30 focus-within:border-[#c3a66d] py-2 md:w-96">
            <Search size={18} className="text-[#a6894f]" />
            <input data-testid="admin-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, phone, destination…" className="bg-transparent outline-none flex-1 text-[#053e49] font-serif text-lg" />
          </label>
        </div>
        <div data-testid="admin-enquiry-list" className={`mt-10 grid gap-5 transition-opacity ${loading ? 'opacity-50' : 'opacity-100'}`}>
          {items.map((e) => <EnquiryCard key={e.id} enquiry={e} />)}
          {!loading && items.length === 0 && <p data-testid="admin-empty-state" className="text-[#6b7c80] font-serif text-lg">No enquiries found.</p>}
        </div>
      </main>
    </div>
  );
}
